import { ProjectGet, Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { ApiSliceParams } from "../interfaces";
import { createListController, getIdWithCompany } from "../utils";
import { Project, ProjectAddressInfo } from "./interfaces";

const PROJECT_INACTIVE = 9;

export const PROJECT_BLOCKED_STATUS = 8;
export const PROJECT_NON_STANDARD = 4;
export const PROJECT_EXTERNAL = 0;
export const PROJECT_INTERNAL = 1;
export const FEECODE_NON_STANDARD = 900;
export const LOB_NON_STANDARD = 90;

export const makeProject = ({ connection, company }: ApiSliceParams) => {
    const infoCache: Record<string, Promise<ProjectAddressInfo | null>> = {};

    const info = (project: Project) => {
        const key = getIdWithCompany(project._id, project.company);

        if (!(key in infoCache)) {
            infoCache[key] = (async () => {
                const items = connection.value.project.info({
                    company: project.company,
                    id: [project._id],
                });

                for await (const item of items) {
                    return item;
                }

                return null;
            })().catch((err: unknown) => {
                delete infoCache[key];
                throw err;
            });
        }

        return infoCache[key];
    };

    const {
        items: fullProjectsList,
        loading: getLoading,
        loaded: getLoaded,
        listen: startListen,
        get,
        getOrLoad: baseGetOrLoad,
    } = createListController<
        ProjectGet,
        Project,
        { active: boolean; company?: string }
    >({
        name: "project",
        connection,
        getId: (item, filter) =>
            getIdWithCompany(item.id, filter.company ?? company.value),
        view: (item, filter) => ({
            ...item,
            id: getIdWithCompany(item.id, filter.company ?? company.value),
            _id: item.id,
            active: item.status !== PROJECT_INACTIVE,
            time_code_list_id: item.time_code_list_id?.equals(Ulid.ZERO)
                ? undefined
                : item.time_code_list_id,
            company: filter.company ?? company.value,
        }),
        load: (con, id, filter) =>
            con.project.get({
                id: Array.isArray(id) ? id : [id],
                company: filter.company ?? company.value,
            }),
        list: (con, { active, company: cmp }) =>
            con.project.items({
                company: cmp ?? company.value,
                groups: [],
                active,
            }),
        getKey: ({ active, company: cmp = company.value }) =>
            `${cmp}-${active ? "1" : "0"}`,
    });
    const allProjects = computed(() =>
        fullProjectsList.value.filter(
            (p) => p.status !== PROJECT_BLOCKED_STATUS,
        ),
    );
    const projects = computed(() => allProjects.value.filter((p) => p.active));
    let loadingSignal: ReadonlySignal<boolean>;
    let loadedSignal: ReadonlySignal<boolean>;
    let inactiveLoadingSignal: ReadonlySignal<boolean>;

    const lookup = (query: string | null | undefined) => {
        if (!query) {
            return null;
        }

        const byId = allProjects.value.find(
            (project) =>
                project.id === query || project._id.toString() === query,
        );

        if (byId) {
            return byId;
        }

        const finalQuery = query.toUpperCase().replace(/-|\b0+/g, "");

        return allProjects.value.find(
            (p) => `${p.client}${p.project}` === finalQuery,
        );
    };

    const listen = () => startListen({ active: true });

    const listenCompany = (cmp: string) =>
        startListen({ active: true, company: cmp });

    const listenInactive = () => startListen({ active: false });

    const getOrLoad = (id: string | Readonly<Ulid>, company: string) =>
        baseGetOrLoad(id, { active: true, company });

    const getWithCompany = (
        id: string | Readonly<Ulid> | null | undefined,
        cmp = company.value,
    ) => (id ? get(getIdWithCompany(id, cmp)) : null);

    return {
        projects,
        allProjects,
        info,

        get loading() {
            if (!loadingSignal) {
                loadingSignal = getLoading({
                    active: true,
                    company: company.value,
                });
            }

            return loadingSignal;
        },

        get loaded() {
            if (!loadedSignal) {
                loadedSignal = getLoaded({
                    active: true,
                    company: company.value,
                });
            }

            return loadedSignal;
        },

        get inactiveLoading() {
            if (!inactiveLoadingSignal) {
                inactiveLoadingSignal = getLoaded({
                    active: false,
                    company: company.value,
                });
            }

            return inactiveLoadingSignal;
        },

        lookup,
        get,
        getWithCompany,
        getOrLoad,

        listen,
        listenInactive,
        listenCompany,
    };
};
